import * as Yup from 'yup';
import React from 'react';
import { Formik, FormikProps } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';

import { taskTemplatesKey } from '../../config';
import { TaskTemplateDTO } from '../../types/task';
import taskService from '../../services/taskService';

import TemplateForm from './TemplateForm';
import Drawer from '../Drawer';
import Spinner from '../../Spinner';

type NewTemplateDrawerDrawerProps = {
  onClose: () => void;
  open: boolean;
};

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  activity: Yup.string().required('Activity is required'),
  tasks: Yup.array().of(Yup.string().required('Task name is required')),
});

const NewTemplateDrawer: React.FC<NewTemplateDrawerDrawerProps> = props => {
  const queryClient = useQueryClient();
  const createTask = useMutation(taskService.createTaskTemplate, {
    onSuccess: async () => {
      toast.success('The task template has been successfully created!');
      await queryClient.refetchQueries(taskTemplatesKey.all);
    },
    onError: () => {
      toast.error('Something went wrong. Try again!');
    },
  });

  const initialValues: TaskTemplateDTO = {
    name: '',
    activity: '',
    tasks: [''],
  };

  const handleOnSubmit = async (values: TaskTemplateDTO) => {
    try {
      await createTask.mutateAsync(values);
      props.onClose();
    } catch (e) {
      //
    }
  };

  return (
    <Drawer open={props.open} onClose={props.onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleOnSubmit}
      >
        {({ isSubmitting, dirty }: FormikProps<TaskTemplateDTO>) => (
          <Drawer.FormContainer
            open={props.open}
            onClose={props.onClose}
            title="New Template"
            description="Create task templates for repeat actions"
            Footer={
              <>
                <button
                  className="btn btn-white"
                  onClick={props.onClose}
                  disabled={isSubmitting}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary ml-4"
                  type="submit"
                  disabled={isSubmitting || !dirty}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        className="h-5 w-5 text-white"
                        aria-hidden="true"
                      />
                      Loading
                    </>
                  ) : (
                    'Create'
                  )}
                </button>
              </>
            }
          >
            <TemplateForm isSubmitting={isSubmitting} />
          </Drawer.FormContainer>
        )}
      </Formik>
    </Drawer>
  );
};

export default NewTemplateDrawer;
